import { useCallback, useMemo, useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import type { PcmpDivision } from '../types/pcmpQueue.types';
import type {
  PcmpMoReportFilters,
  PcmpMoReportSearch,
  PcmpMoScope,
} from '../types/pcmpMoReport.types';

const route = getRouteApi('/_auth/pcmp/reports/mo-wise/');

/** A picked person: the report's filters carry the id, the picker needs the name. */
export interface PcmpPickedUser {
  id: number;
  name: string;
  /** Known once picked; a pick restored from the URL leaves it out. */
  isActive?: boolean | undefined;
}

interface PcmpMoDraftFilters {
  scope: PcmpMoScope;
  mo: PcmpPickedUser | null;
  reporting: PcmpPickedUser | null;
  division: PcmpDivision | undefined;
  departmentId: number | undefined;
  projectIds: number[];
  dateFrom: string;
  dateTo: string;
}

/** The name a pick has until the report (or the picker) supplies the real one. */
export const pcmpPlaceholderName = (id: number): string => `User #${id}`;

const placeholder = (id: number | undefined): PcmpPickedUser | null =>
  id === undefined ? null : { id, name: pcmpPlaceholderName(id) };

/** The same person keeps the name they were picked with. */
const keepName = (previous: PcmpPickedUser | null, next: PcmpPickedUser | null) =>
  previous?.id === next?.id ? (previous ?? next) : next;

const INITIAL_FILTERS: PcmpMoDraftFilters = {
  scope: 'MO',
  mo: null,
  reporting: null,
  division: undefined,
  departmentId: undefined,
  projectIds: [],
  dateFrom: '',
  dateTo: '',
};

/**
 * The MO Wise report's draft-then-apply filters. Scope MO takes one MO, scope
 * ALL narrows by manager, division and region; the other scope's fields are
 * left out of the applied filters.
 */
export function usePcmpMoReportFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const scope: PcmpMoScope = search.scope ?? 'MO';

  const appliedFilters = useMemo<PcmpMoReportFilters>(
    () =>
      scope === 'MO'
        ? {
            scope,
            moUserId: search.moUserId,
            projectIds: search.projectIds,
            dateFrom: search.dateFrom,
            dateTo: search.dateTo,
          }
        : {
            scope,
            reportingUserId: search.reportingUserId,
            departmentId: search.departmentId,
            division: search.division,
            projectIds: search.projectIds,
            dateFrom: search.dateFrom,
            dateTo: search.dateTo,
          },
    [
      scope,
      search.moUserId,
      search.reportingUserId,
      search.departmentId,
      search.division,
      search.projectIds,
      search.dateFrom,
      search.dateTo,
    ],
  );

  const appliedDraft = useMemo<PcmpMoDraftFilters>(
    () => ({
      scope,
      mo: placeholder(appliedFilters.moUserId),
      reporting: placeholder(appliedFilters.reportingUserId),
      division: appliedFilters.division,
      departmentId: appliedFilters.departmentId,
      projectIds: appliedFilters.projectIds ?? [],
      dateFrom: appliedFilters.dateFrom ?? '',
      dateTo: appliedFilters.dateTo ?? '',
    }),
    [scope, appliedFilters],
  );

  const [draft, setDraftState] = useState<PcmpMoDraftFilters>(appliedDraft);
  const [syncedDraft, setSyncedDraft] = useState(appliedDraft);

  // The URL changed (Apply, Reset, back, a shared link): the draft follows it,
  // keeping the names of the people already picked.
  if (appliedDraft !== syncedDraft) {
    setSyncedDraft(appliedDraft);
    setDraftState({
      ...appliedDraft,
      mo: keepName(draft.mo, appliedDraft.mo),
      reporting: keepName(draft.reporting, appliedDraft.reporting),
    });
  }

  const activeFilterCount = useMemo(
    () =>
      [
        appliedFilters.moUserId,
        appliedFilters.reportingUserId,
        appliedFilters.departmentId,
        appliedFilters.division,
        appliedFilters.projectIds?.length ? appliedFilters.projectIds : undefined,
        appliedFilters.dateFrom,
        appliedFilters.dateTo,
      ].filter((value) => value !== undefined).length,
    [appliedFilters],
  );

  const isDateRangeInvalid =
    draft.dateFrom !== '' && draft.dateTo !== '' && draft.dateFrom > draft.dateTo;

  const setDraft = useCallback(
    <K extends keyof PcmpMoDraftFilters>(key: K, value: PcmpMoDraftFilters[K]) => {
      setDraftState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleApply = useCallback(() => {
    if (isDateRangeInvalid) return;
    const next: PcmpMoReportSearch = {
      scope: draft.scope === 'MO' ? undefined : draft.scope,
      moUserId: draft.scope === 'MO' ? draft.mo?.id : undefined,
      reportingUserId: draft.scope === 'ALL' ? draft.reporting?.id : undefined,
      departmentId: draft.scope === 'ALL' ? draft.departmentId : undefined,
      division: draft.scope === 'ALL' ? draft.division : undefined,
      projectIds: draft.projectIds.length ? draft.projectIds : undefined,
      dateFrom: draft.dateFrom || undefined,
      dateTo: draft.dateTo || undefined,
    };
    void navigate({ search: next });
  }, [draft, isDateRangeInvalid, navigate]);

  const handleReset = useCallback(() => {
    setDraftState(INITIAL_FILTERS);
    void navigate({ search: {} });
  }, [navigate]);

  return {
    filters: draft,
    scope,
    appliedFilters,
    activeFilterCount,
    isDateRangeInvalid,
    setDraft,
    handleApply,
    handleReset,
  };
}

export type PcmpMoReportFilterController = ReturnType<typeof usePcmpMoReportFilters>;
